// =============================================================================
// Custom Error Types
// =============================================================================

export class ConflictError extends Error {
  status = 409
  data: any

  constructor(message: string, data: any) {
    super(message)
    this.name = 'ConflictError'
    this.data = data
  }
}

// =============================================================================
// Base API Client
// =============================================================================

const API_BASE_URL = (() => {
  const raw = import.meta.env.VITE_API_BASE_URL
  if (!raw) {
    throw new Error('VITE_API_BASE_URL environment variable is required')
  }
  return raw.endsWith('/') ? raw : `${raw}/`
})()

const API_TIMEOUT = 30000

// Build URL with optional query params
const buildUrl = (endpoint: string, params?: Record<string, any>): URL => {
  const url = new URL(endpoint.replace(/^\//, ''), API_BASE_URL)
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        // Handle arrays by joining with commas
        url.searchParams.append(key, Array.isArray(value) ? value.join(',') : String(value))
      }
    })
  }
  return url
}

// Get JWT token from Amplify
const getAuthToken = async (): Promise<string | null> => {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth')
    const session = await fetchAuthSession()
    return session.tokens?.idToken?.toString() || null
  } catch (error) {
    console.error('Failed to get auth token:', error)
    return null
  }
}

// Extract error message from FastAPI response
const extractErrorMessage = (errorData: any, fallback: string): string => {
  if (!errorData?.detail) return fallback
  if (typeof errorData.detail === 'string') return errorData.detail
  if (Array.isArray(errorData.detail)) {
    return errorData.detail.map((e: any) => e.msg || JSON.stringify(e)).join(', ')
  }
  // Object detail (e.g. a {field: message} validation map) — surface the
  // messages rather than dropping them for the generic fallback.
  if (typeof errorData.detail === 'object') {
    const messages = Object.values(errorData.detail).filter((v): v is string => typeof v === 'string')
    if (messages.length) return messages.join(', ')
  }
  return fallback
}

// Core fetch wrapper with auth, timeout, and error handling
export const request = async <T = any>(
  endpoint: string,
  options: {
    method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
    params?: Record<string, any>
    body?: any
    timeout?: number
    isFormData?: boolean
  } = {}
): Promise<T> => {
  const { method = 'GET', params, body, timeout = API_TIMEOUT, isFormData = false } = options
  const url = buildUrl(endpoint, params)

  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    const token = await getAuthToken()

    const headers: Record<string, string> = {}
    if (token) headers['Authorization'] = `Bearer ${token}`
    if (!isFormData) headers['Content-Type'] = 'application/json'

    const response = await fetch(url.toString(), {
      method,
      headers,
      body: isFormData ? body : body ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      if (response.status === 401) throw new Error('Unauthorized access')
      if (response.status >= 500) throw new Error('Server error')

      const errorData = await response.json().catch(() => ({}))
      if (response.status === 409) {
        throw new ConflictError(
          extractErrorMessage(errorData, 'Conflict'),
          errorData
        )
      }
      throw new Error(extractErrorMessage(errorData, `HTTP error! status: ${response.status}`))
    }

    if (response.status === 204) {
      return undefined as T
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return undefined as T
    }

    return await response.json()
  } catch (error: any) {
    clearTimeout(timeoutId)
    if (error.name === 'AbortError') throw new Error('Request timeout')
    throw error
  }
}

// =============================================================================
// Public API Methods
// =============================================================================

export const api = {
  get: <T = any>(endpoint: string, params?: Record<string, any>) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'POST', body }),

  put: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'PUT', body }),

  patch: <T = any>(endpoint: string, body?: any) =>
    request<T>(endpoint, { method: 'PATCH', body }),

  delete: <T = any>(endpoint: string, params?: Record<string, any>) =>
    request<T>(endpoint, { method: 'DELETE', params }),

  upload: <T = any>(endpoint: string, formData: FormData) =>
    request<T>(endpoint, { method: 'POST', body: formData, isFormData: true }),
}

// =============================================================================
// Matches - Complex param building kept as wrapper
// =============================================================================

export const fetchMatches = (params?: {
  start_date?: string
  end_date?: string
  player_ids?: string[]
  competition_ids?: string[]
  categories?: string[]
  publishing_statuses?: string[]
  venue_ids?: string[]
  search?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  limit?: number
}) => {
  const apiParams: Record<string, any> = {}
  if (params?.start_date) apiParams.start_date = params.start_date
  if (params?.end_date) apiParams.end_date = params.end_date
  if (params?.player_ids?.length) apiParams.player_ids = params.player_ids
  if (params?.competition_ids?.length) apiParams.competition_ids = params.competition_ids
  if (params?.categories?.length) apiParams.categories = params.categories
  if (params?.publishing_statuses?.length) apiParams.publishing_statuses = params.publishing_statuses
  if (params?.venue_ids?.length) apiParams.venue_ids = params.venue_ids
  if (params?.search?.trim()) apiParams.search = params.search.trim()
  if (params?.sort_order) apiParams.sort_order = params.sort_order
  if (params?.page) apiParams.page = params.page
  if (params?.limit) apiParams.limit = params.limit

  return api.get('/matches', apiParams)
}

export const updateMatch = (matchId: string, data: any) =>
  api.put(`/matches/${matchId}`, data)

export const createMatch = (data: Record<string, any>) =>
  api.post('/matches', data)

// =============================================================================
// Feed Management
// =============================================================================

export const updateVenueFeedMappings = (mappings: Array<{ feed_id: string; venue_id: string; season_id: string }>) =>
  api.put('/publishing/venue-feed-mappings', { mappings })

// =============================================================================
// Image Management
// =============================================================================

type EntityType = 'match' | 'player' | 'tournament' | 'competitionLevel' | 'vod' | 'widget' | 'feed'

const getEntityEndpoint = (entityType: EntityType, entityId: string): string => {
  // Record<EntityType, ...> makes a missing entity a compile error.
  const entityMap: Record<EntityType, string> = {
    match: `matches/${entityId}`,
    player: `players/${entityId}`,
    competitionLevel: `competition-levels/${entityId}`,
    tournament: `competitions/tournaments/${entityId}`,
    vod: `vod/assets/${entityId}`,
    widget: `merchandising/widgets/${entityId}`,
    feed: `feeds/${entityId}`,
  }
  return entityMap[entityType]
}

export const uploadImage = (
  entityType: EntityType,
  entityId: string,
  file: File,
  metadata?: { title?: string; caption?: string; alt_text?: string; image_type?: string }
) => {
  const formData = new FormData()
  formData.append('file', file)
  if (metadata?.title) formData.append('title', metadata.title)
  if (metadata?.caption) formData.append('caption', metadata.caption)
  if (metadata?.alt_text) formData.append('alt_text', metadata.alt_text)
  if (metadata?.image_type) formData.append('image_type', metadata.image_type)

  return api.upload<{ url: string }>(`/${getEntityEndpoint(entityType, entityId)}/images`, formData)
}

// Hero banner slides store an image by URL in widget metadata (not the
// tc_image_link slot model), so each upload just returns a CDN URL the caller
// writes into metadata.slides[].image_url. See POST /widgets/:id/hero-images.
export const uploadHeroImage = (widgetId: string, file: File, altText?: string) => {
  const formData = new FormData()
  formData.append('file', file)
  if (altText) formData.append('alt_text', altText)
  return api.upload<{ cdn_url: string; tc_image_id: string }>(
    `/merchandising/widgets/${widgetId}/hero-images`,
    formData,
  )
}

export const fetchImages = (entityType: EntityType, entityId: string) =>
  api.get(`/${getEntityEndpoint(entityType, entityId)}/images`)

export const generateMatchcard = (matchId: string) =>
  api.post(`/matches/${matchId}/generate-matchcard`)

// =============================================================================
// Players
// =============================================================================

export const createPlayer = (data: {
  tc_name: string
  tc_short_name?: string | null
  tc_abbreviation?: string | null
  tc_country_code?: string | null
}) =>
  api.post('/players', data)

export const updatePlayer = (playerId: string, data: {
  tc_name?: string | null
  tc_short_name?: string | null
  tc_abbreviation?: string | null
  tc_country_code?: string | null
}) =>
  api.patch(`/players/${playerId}`, data)

export const createTeam = (data: {
  player1_tc_competitor_id: string
  player2_tc_competitor_id: string
  tc_name: string
  tc_abbreviation?: string | null
}) =>
  api.post('/teams', data)

export const deleteImage = (entityType: EntityType, entityId: string, imageId: string, reason?: string) =>
  request(`/${getEntityEndpoint(entityType, entityId)}/images`, {
    method: 'DELETE',
    params: { image_id: imageId },
    body: reason ? { reason } : undefined,
  })

export const fetchCompetitionLevels = () =>
  api.get('/competition-levels')

// =============================================================================
// Competitions & Seasons
// =============================================================================

export interface TournamentSettingsChild {
  tc_competition_id?: string  // If present, update existing; if absent, create new
  tc_name?: string
  short_name?: string
  tc_type?: string
  tc_gender?: string
  category?: string
}

export const updateTournamentSettings = (
  parentId: string,
  data: {
    tc_name?: string
    tc_official_name?: string | null
    tc_latest_city?: string | null
    tc_latest_country_code?: string | null
    tc_latest_timezone?: string | null
    is_hidden?: boolean
    children?: TournamentSettingsChild[]
  }
) =>
  api.put(`/competitions/parents/${parentId}/settings`, data)

export interface CreateTournamentData {
  tc_name: string
  children: TournamentSettingsChild[]
  create_edition?: boolean
  edition_year?: number
}

export interface CreateTournamentResponse {
  message: string
  tc_competition_parent_id: string
  tc_name: string
  children_created: Array<{ tc_competition_id: string; tc_name: string }>
  seasons_created: Array<{ tc_season_id: string; tc_competition_id: string; tc_name: string }>
  edition_year: number | null
  timestamp: string
}

export interface SeasonBackgroundImage {
  tc_image_id: string
  cdn_url: string
  thumbnail_url?: string
  pixel_width?: number
  pixel_height?: number
  aspect_ratio?: string
  mime_type?: string
  created_at?: string
}

export const createTournament = (data: CreateTournamentData): Promise<CreateTournamentResponse> =>
  api.post('/competitions/parents', data)

export const updateSeason = (seasonId: string, data: {
  tc_name?: string
  tc_year?: number
  tc_start_date?: string | null
  tc_end_date?: string | null
  tc_complex_id?: string | null
}) =>
  api.put(`/seasons/${seasonId}`, data)

export const createSeasonsBulk = (data: {
  parent_id: string
  year: number
  seasons: Array<{
    tc_competition_id: string
    tc_start_date?: string
    tc_end_date?: string
    tc_name?: string
  }>
}) =>
  api.post('/seasons/bulk', data)

export const deleteSeason = (seasonId: string) =>
  api.delete(`/seasons/${seasonId}`)

export const uploadSeasonBackground = (
  seasonId: string,
  file: File
): Promise<SeasonBackgroundImage> => {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<SeasonBackgroundImage>(`/seasons/${seasonId}/background`, formData)
}

export const deleteSeasonBackground = (seasonId: string): Promise<void> =>
  api.delete(`/seasons/${seasonId}/background`)

export const updateSeasonLicense = (seasonId: string, isLicensed: boolean) =>
  api.patch(`/seasons/${seasonId}/license`, { is_licensed: isLicensed })

export const bulkUpdateSeasonLicense = (data: {
  is_licensed: boolean
  competition_id?: string
  season_ids?: string[]
}) =>
  api.patch('/seasons/bulk-license', data)

export const deleteChildCompetition = (competitionId: string) =>
  api.delete(`/competitions/children/${competitionId}`)

// =============================================================================
// Complexes & Venues
// =============================================================================

export const createComplex = (data: { tc_name: string }) =>
  api.post('/complexes', data)

export const updateComplex = (complexId: string, data: { tc_name?: string }) =>
  api.put(`/complexes/${complexId}`, data)

export const createVenue = (data: {
  tc_complex_id: string
  tc_name: string
  tc_city?: string
  tc_country_code?: string
  tc_timezone?: string
}) =>
  api.post('/venues', data)

export const updateVenue = (venueId: string, data: {
  tc_name?: string
  tc_city?: string
  tc_country_code?: string
  tc_timezone?: string
}) =>
  api.put(`/venues/${venueId}`, data)

export const deleteVenue = (venueId: string) =>
  api.delete(`/venues/${venueId}`)

// =============================================================================
// Match VOD Count
// =============================================================================

export interface VodTypeInfo {
  tc_vod_asset_id: string
  publish_status: string
}

export interface MatchVodCountResponse {
  count: number
  status_counts: Record<string, number>
  tc_match_id: string
  vods: {
    highlight?: VodTypeInfo
    condensed?: VodTypeInfo
  }
}

export const fetchMatchVodCount = (matchId: string): Promise<MatchVodCountResponse> =>
  api.get(`/matches/${matchId}/vod-count`)

// Carousel live preview — resolves an (unsaved) carousel config to its cards
// via ACE's previewWidgetItems, proxied server-side by the CMS API.
export interface PreviewCard {
  cardId: string
  title: string
  subtitle?: string | null
  roundCode?: string | null
  link?: string | null
  badge?: string | null
  startTime?: string | null
  duration?: string | null
  matchScoreKey?: string | null
  image?: { cdnUrl?: string; tcMediaUrl?: string | null; altText?: string | null } | null
  cursor?: string | null
}

export interface CarouselPreviewResponse {
  cards: PreviewCard[]
  has_next_page: boolean
  next_cursor: string | null
}

export const fetchCarouselPreview = (siteId: string, config: Record<string, unknown>, first?: number) =>
  api.post<CarouselPreviewResponse>('/merchandising/carousel-preview', { site_id: siteId, config, first })

export interface LinkShoulderVodsResponse {
  match_id: string
  created_virtual_containers: Array<{ vod_type_code: string; tc_vod_asset_id: string }>
  linked_wsc_assets: Array<{ tc_vod_asset_id: string; vod_type_code: string; tc_title: string }>
  updated_wsc_assets: Array<{ tc_vod_asset_id: string; vod_type_code: string }>
}

export const linkShoulderVods = (matchId: string): Promise<LinkShoulderVodsResponse> =>
  api.post(`/matches/${matchId}/workflow/link-shoulder-vods`)

// =============================================================================
// Live Control
// =============================================================================

export interface LiveControlParams {
  status_filter?: string
  hours_upcoming?: number
  hours_recent?: number
  search?: string
  player_ids?: string
  categories?: string
  sort_order?: 'asc' | 'desc'
  page?: number
  limit?: number
}

export const fetchLiveControlMatches = (params?: LiveControlParams) =>
  api.get('/live-control', params)

// =============================================================================
// Match Documents
// =============================================================================

export const uploadMatchBriefing = (file: File) => {
  const formData = new FormData()
  formData.append('file', file)
  return api.upload<{
    message: string
    briefing_id: string
    s3_key: string
    file_name: string
    file_size: number
    uploaded_by: string
    uploaded_at: string
  }>('/match-briefing/upload', formData)
}

export interface MatchBriefing {
  id: string
  tournament_title: string
  tournament_dates: string | null
  match_note_date?: string | null
  tournament_day: string | null
  level: string | null
  prize_money: string | null
  html_s3_key: string | null
  html_url?: string | null
  pdf_s3_key?: string
  original_filename: string | null
  uploaded_by: string | null
  created_at: string | null
  updated_at: string | null
}

export interface MatchBriefingsResponse {
  briefings: MatchBriefing[]
  pagination: {
    page: number
    limit: number
    total_count: number
    total_pages: number
    has_next: boolean
    has_prev: boolean
  }
  filters: { days: number }
}

export const fetchMatchBriefings = (params?: { days?: number; page?: number; limit?: number }) =>
  api.get<MatchBriefingsResponse>('/match-briefing/briefings', params)

export const fetchMatchBriefing = (id: string) =>
  api.get<MatchBriefing>(`/match-briefing/briefings/${id}`)

// =============================================================================
// Merchandising
// =============================================================================

export interface Site {
  id: string
  code: string
  name: string
}

export interface Region {
  id: string
  site_id: string
  code: string
  name: string
  scope: 'global' | 'page'
  sort_order: number
}

export interface Widget {
  id: string
  site_id?: string
  widget_type: string
  name: string
  metadata: Record<string, any>
  /** Populated for banner_image widgets with an uploaded desktop image. */
  preview_url?: string | null
  created_at?: string | null
  created_by?: string | null
  updated_at?: string | null
  updated_by?: string | null
}

export interface PageRecord {
  id: string
  version: number
  is_live: boolean
  site_id: string
  page_type: string
  path: string
  name: string
  is_custom: boolean
  is_active: boolean
  title: string | null
  description: string | null
  created_at?: string | null
  updated_at?: string | null
  draft_updated_at?: string | null
}

export interface PlacementSummary {
  id: string
  version: number
  is_live: boolean
  is_deleted: boolean
  name: string
  sort_order: number
  pinned_position: number | null
  widget: Widget
  region: { id: string; code: string; name: string; scope: string }
  site: { id: string; code: string; name: string }
  page: { id: string; path: string; name: string } | null
  page_type: string | null
  /** Tournament/player slug this placement targets, or null for page-type-wide. */
  slug: string | null
  device_targets: string[]
  starts_at: Date | null
  ends_at: Date | null
  is_active: boolean
  created_at: Date
  created_by: string | null
  updated_at: Date | null
  draft_updated_at?: string | null
}

export interface WidgetPlacementSummary extends PlacementSummary {
  is_currently_active: boolean
}

export interface DraftStatus {
  has_draft: boolean
  updated_by?: string | null
  updated_at?: string | null
}

export interface DraftScope {
  page_type?: string | null
  page_id?: string | null
  updated_by?: string | null
  updated_at?: string | null
}

export interface DraftChange {
  type: 'added' | 'modified' | 'removed'
  name: string
  region: string
  position?: number | null
  details?: string | null
}

export interface PublishRequest {
  site_id: string
  page_type?: string | null
  page_id?: string | null
  draft_updated_at: string
}

export interface UpdatePageRequest {
  name?: string
  path?: string
  is_active?: boolean
  title?: string | null
  description?: string | null
  draft_updated_at: string
}

export interface PlacementImpactExpectation {
  type: string
  placement_id: string
  new_starts_at?: string
  new_ends_at?: string
}

export interface PlacementMutationResponse extends PlacementSummary {
  draft_updated_at?: string | null
}

export interface DeletePlacementResponse {
  deleted: boolean
  id: string
  is_deleted?: boolean
  draft_updated_at?: string | null
}

export interface ReorderPlacementsResponse {
  reordered: boolean
  count: number
  draft_updated_at?: string | null
}

const parseDate = (v: string | null | undefined): Date | null =>
  v ? new Date(v) : null

const parsePlacement = (raw: any): PlacementSummary => ({
  ...raw,
  page_type: raw.targeting_rules?.page_type || null,
  slug: raw.targeting_rules?.slug || null,
  starts_at: parseDate(raw.starts_at),
  ends_at: parseDate(raw.ends_at),
  created_at: new Date(raw.created_at),
  updated_at: parseDate(raw.updated_at),
})

const parseWidgetPlacement = (raw: any): WidgetPlacementSummary => ({
  ...parsePlacement(raw),
  is_currently_active: Boolean(raw.is_currently_active),
})

const serializeDate = (v: Date | string | null | undefined): string | null => {
  if (!v) return null
  return v instanceof Date ? v.toISOString() : v
}

export const fetchSites = () => api.get<{ sites: Site[] }>('/merchandising/sites')

export interface PageTypeConfig {
  page_types: Record<string, string[]>  // page_type (or "__all__" for site-wide) → region codes
}

export const fetchPageTypeConfig = (siteId: string) =>
  api.get<PageTypeConfig>('/merchandising/page-type-config', { site_id: siteId })

export const fetchRegions = (siteId: string) =>
  api.get<{ regions: Region[] }>('/merchandising/regions', { site_id: siteId })

export const fetchWidgets = (siteId: string, widgetType?: string) =>
  api.get<{ widgets: Widget[] }>('/merchandising/widgets', { site_id: siteId, widget_type: widgetType || undefined })

export const fetchWidget = (widgetId: string) =>
  api.get<Widget>(`/merchandising/widgets/${widgetId}`)

export const createWidget = (data: { site_id: string; widget_type: string; name: string; metadata?: Record<string, any> }) =>
  api.post<Widget>('/merchandising/widgets', data)

export const updateWidget = (widgetId: string, data: { name?: string; widget_type?: string; metadata?: Record<string, any> }) =>
  api.put<Widget>(`/merchandising/widgets/${widgetId}`, data)

export const deleteWidget = (widgetId: string) =>
  api.delete(`/merchandising/widgets/${widgetId}`)

// tc_image_type codes for the four banner_image device slots.
export type BannerImageType =
  | 'banner_image_desktop'
  | 'banner_image_tablet'
  | 'banner_image_mobile'
  | 'banner_image_tv'

export const fetchPages = (siteId: string, pageType?: string, includeDrafts = true) =>
  api.get<{ pages: PageRecord[] }>('/merchandising/pages', {
    site_id: siteId,
    page_type: pageType || undefined,
    // Default view (CMS) returns the highest version per page (draft if any).
    // Pass false to get only published rows — what tennis.com actually serves.
    include_drafts: includeDrafts,
  })

export const createPage = (data: {
  site_id: string
  page_type: string
  path: string
  name: string
  is_custom?: boolean
  is_active?: boolean
  title?: string | null
  description?: string | null
}) =>
  api.post<PageRecord>('/merchandising/pages', data)

export const updatePage = (pageId: string, data: UpdatePageRequest) =>
  api.put<PageRecord>(`/merchandising/pages/${pageId}`, data)

export const deletePage = (pageId: string) =>
  api.delete(`/merchandising/pages/${pageId}`)

export const fetchPageDraft = (params: {
  site_id: string
  page_type?: string
  page_id?: string
}) => api.get<DraftStatus>('/merchandising/page-draft', params)

export const fetchPageDrafts = (siteId: string) =>
  api.get<{ drafts: DraftScope[] }>('/merchandising/page-drafts', { site_id: siteId })

export const fetchDraftChanges = (params: {
  site_id: string
  page_type?: string
  page_id?: string
}) => api.get<{ changes: DraftChange[] }>('/merchandising/draft-changes', params)

export const publishPage = (data: PublishRequest) =>
  api.post<{ published: boolean }>('/merchandising/publish', data)

export const revertPage = (data: PublishRequest) =>
  api.post<{ reverted: boolean }>('/merchandising/revert', data)

export interface PreviewTokenResponse {
  token: string
  expires_at: string
}

// Short-lived HMAC token gating preview access on ACE. Validated by
// apps/ace/internal/service/preview_token.go using a shared secret.
export const mintPreviewToken = () =>
  api.post<PreviewTokenResponse>('/merchandising/preview-token', {})

export interface TimelineEvent {
  event_type: 'starts' | 'ends'
  placement_id: string
  placement_name: string
  region_name: string
  position: number
  is_live: boolean
}

export interface TimelineChangePoint {
  timestamp: string
  events: TimelineEvent[]
}

export const fetchTimeline = (params: { site_id: string; page_type?: string; page_id?: string; slug?: string; include_drafts?: boolean }) =>
  api.get<{
    now: string
    change_points: TimelineChangePoint[]
    total_count: number
  }>('/merchandising/timeline', params)

export const fetchPlacements = async (params: {
  site_id: string
  region_id?: string
  page_type?: string
  page_id?: string
  slug?: string
  widget_type?: string
  is_active?: boolean
  device?: string
  include_drafts?: boolean
  /** Containment match — also returns globals when filtering by page_type. Use for preview. */
  include_ancestor_scopes?: boolean
  /** Include placements with ends_at in the past. Default false in the editor. */
  include_expired?: boolean
}) => {
  const raw = await api.get<{
    placements: any[]
  }>('/merchandising/placements', params)
  return {
    ...raw,
    placements: raw.placements.map(parsePlacement),
  }
}

export const fetchPlacement = async (id: string): Promise<PlacementSummary> => {
  const raw = await api.get(`/merchandising/placements/${id}`)
  return parsePlacement(raw)
}

export interface ImpactResult {
  type: string
  placement_id: string
  placement_name: string
  new_ends_at?: string
  new_starts_at?: string
}

export const fetchPreviewImpacts = (params: {
  region_id: string
  sort_order: number
  page_type?: string
  page_id?: string
  slug?: string
  starts_at?: string
  ends_at?: string
  exclude_placement_id?: string
}) => api.get<{ impacts: ImpactResult[] }>('/merchandising/placements/preview-impacts', params)

export const fetchWidgetPlacements = async (widgetId: string): Promise<{ placements: WidgetPlacementSummary[] }> => {
  const raw = await api.get<{ placements: any[] }>(`/merchandising/widgets/${widgetId}/placements`)
  return {
    ...raw,
    placements: raw.placements.map(parseWidgetPlacement),
  }
}

export const createPlacement = (data: {
  region_id: string
  widget_id: string
  sort_order: number
  pinned_position?: number | null
  page_id?: string | null
  page_type?: string | null
  slug?: string | null
  device_targets?: string[]
  starts_at?: Date | string | null
  ends_at?: Date | string | null
  is_active?: boolean
  expected_impacts?: PlacementImpactExpectation[]
  draft_updated_at: string
}) => {
  const targeting_rules: Record<string, string> = {}
  if (data.page_type) targeting_rules.page_type = data.page_type
  if (data.page_id) targeting_rules.page_id = data.page_id
  if (data.slug) targeting_rules.slug = data.slug
  return api.post<PlacementMutationResponse>('/merchandising/placements', {
    ...data,
    targeting_rules,
    starts_at: serializeDate(data.starts_at),
    ends_at: serializeDate(data.ends_at),
  })
}

export const updatePlacement = (id: string, data: {
  region_id?: string
  widget_id?: string
  sort_order?: number
  pinned_position?: number | null
  page_id?: string | null
  page_type?: string | null
  slug?: string | null
  device_targets?: string[]
  starts_at?: Date | string | null
  ends_at?: Date | string | null
  is_active?: boolean
  expected_impacts?: PlacementImpactExpectation[]
  draft_updated_at: string
}) => {
  const hasTargeting = data.page_type !== undefined || data.page_id !== undefined || data.slug !== undefined
  const targeting_rules = hasTargeting ? {
    ...(data.page_type ? { page_type: data.page_type } : {}),
    ...(data.page_id ? { page_id: data.page_id } : {}),
    ...(data.slug ? { slug: data.slug } : {}),
  } : undefined
  return api.put<PlacementMutationResponse>(`/merchandising/placements/${id}`, {
    ...data,
    ...(targeting_rules !== undefined && { targeting_rules }),
    ...(data.starts_at !== undefined && { starts_at: serializeDate(data.starts_at) }),
    ...(data.ends_at !== undefined && { ends_at: serializeDate(data.ends_at) }),
  })
}

export const deletePlacement = (id: string, draftUpdatedAt: string) =>
  api.delete<DeletePlacementResponse>(`/merchandising/placements/${id}`, { draft_updated_at: draftUpdatedAt })

export const reorderPlacements = (
  regionId: string,
  order: Array<{ placement_id: string; sort_order: number }>,
  draftUpdatedAt: string
) =>
  api.post<ReorderPlacementsResponse>('/merchandising/placements/reorder', {
    region_id: regionId,
    order,
    draft_updated_at: draftUpdatedAt,
  })

// =============================================================================
// Slugs
// =============================================================================

export interface SlugRecord {
  tc_slug_id: string
  slug: string
  entity_type: string
  entity_id: string
  is_canonical: boolean
  created_by: string
  created_at: string | null
  owner_name?: string | null
}

export interface SlugListResponse {
  canonical: SlugRecord | null
  redirects: SlugRecord[]
}

export const lookupSlug = (slug: string) =>
  api.get<SlugRecord>('/slugs/lookup', { slug })

export const listSlugs = (entityType: string, entityId: string) =>
  api.get<SlugListResponse>('/slugs', { entity_type: entityType, entity_id: entityId })

export const createSlug = (payload: {
  entity_type: string
  entity_id: string
  slug: string
  is_canonical: boolean
}) => api.post<SlugRecord>('/slugs', payload)

export const promoteSlug = (tcSlugId: string) =>
  api.patch<SlugRecord>(`/slugs/${tcSlugId}`, { is_canonical: true })

export const deleteSlug = (tcSlugId: string) =>
  api.delete<{ deleted: boolean; tc_slug_id: string }>(`/slugs/${tcSlugId}`)

// =============================================================================
// Feeds & Channels
// =============================================================================

export interface FeedSummary {
  feed_id: string
  name: string
  display_name: string | null
  is_linear: boolean
  is_gated: boolean
  epg_url: string | null
  hls_url: string | null
  dash_url: string | null
  channel_card_url: string | null
  channel_card_image_id: string | null
  has_channel_card: boolean
  published: boolean
}

export interface FeedTags {
  entity_type: string
  entity_id: string
  purpose: string
  tags: string[]
}

export interface FeedPublishState {
  feed_id: string
  tc_pub_content_id: string
  short_id: string
  status: string
  published_at: string | null
}

export const fetchFeedCatalog = () =>
  api.get<{ feeds: FeedSummary[] }>('/feeds/catalog')

export const fetchFeed = (feedId: string) =>
  api.get<FeedSummary>(`/feeds/${feedId}`)

// Editorial settings (display_name, is_gated) plus the stream manifests
// (hls_url, dash_url) are writable; epg_url, drm_license_url, and the routing
// fields are rejected server-side (closed PATCH model). Send null on a manifest
// to clear it (playback falls back to the name-derived Amagi URL).
export const updateFeed = (
  feedId: string,
  data: {
    display_name?: string | null
    is_gated?: boolean
    hls_url?: string | null
    dash_url?: string | null
  },
) => api.patch<FeedSummary>(`/feeds/${feedId}`, data)

export const fetchFeedTags = (feedId: string) =>
  api.get<FeedTags>(`/feeds/${feedId}/tags`)

export const setFeedTags = (feedId: string, tags: string[]) =>
  api.put<FeedTags>(`/feeds/${feedId}/tags`, { tags })

export const publishFeed = (feedId: string) =>
  api.post<FeedPublishState>(`/feeds/${feedId}/publish`)

export const unpublishFeed = (feedId: string) =>
  api.post<FeedPublishState>(`/feeds/${feedId}/unpublish`)

// =============================================================================
// MVPD Providers (Subscriptions & Access)
// =============================================================================

export interface MvpdProvider {
  provider_id: string
  display_name: string       // coalesce(tc_display_name, ext_display_name)
  ext_display_name: string
  tc_display_name: string | null
  logo_url: string | null    // Adobe's logo (read-only in v1)
  enabled: boolean
  promoted: boolean
}

export interface MvpdProvidersResponse {
  providers: MvpdProvider[]
  last_synced_at: string | null
}

export const fetchMvpdProviders = () =>
  api.get<MvpdProvidersResponse>('/mvpd-providers')

// Editorial columns only; ext_* are owned by the sync Lambda (rejected server-side).
export const updateMvpdProvider = (
  providerId: string,
  data: { enabled?: boolean; promoted?: boolean; tc_display_name?: string | null }
) => api.patch<MvpdProvider>(`/mvpd-providers/${encodeURIComponent(providerId)}`, data)
